import * as THREE from 'three';
import type { System } from '../ServiceLocator';

interface WarmupRenderer {
  initTexture(tex: THREE.Texture): void;
  compileAsync(scene: THREE.Scene, camera: THREE.Camera): Promise<unknown>;
}

const TEXTURE_SLOTS = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'emissiveMap', 'envMap', 'lightMap', 'alphaMap', 'bumpMap', 'displacementMap'] as const;

export class AssetSystem implements System {
  readonly name = 'AssetSystem';

  // UE5 PSO precaching pattern: wait up to a timeout, then open the Race
  // gate regardless. Without this, a slow Firefox sync compile would lock
  // the user on the title screen indefinitely.
  static readonly WARMUP_TIMEOUT_MS = 3000;

  static initSceneTextures(renderer: Pick<WarmupRenderer, 'initTexture'>, scene: THREE.Scene): void {
    const seen = new Set<THREE.Texture>();
    const initIfTexture = (value: unknown): void => {
      if (value && typeof value === 'object' && 'isTexture' in value && (value as THREE.Texture).isTexture) {
        const tex = value as THREE.Texture;
        if (seen.has(tex)) return;
        seen.add(tex);
        try { renderer.initTexture(tex); } catch { /* GPU upload best-effort */ }
      }
    };
    scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const mat of mats) {
        if (!mat) continue;
        for (const slot of TEXTURE_SLOTS) initIfTexture((mat as unknown as Record<string, unknown>)[slot]);
      }
    });
  }

  static compileWithTimeout(
    renderer: Pick<WarmupRenderer, 'compileAsync'>,
    scene: THREE.Scene,
    camera: THREE.Camera,
    timeoutMs: number,
  ): Promise<unknown> {
    return Promise.race([
      renderer.compileAsync(scene, camera),
      new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
  }

  warmupShadersAfter(
    racerVisualReady: Promise<void>,
    renderer: WarmupRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera,
    hooks: {
      onWarmupStart?: () => void;
      onShadersCompiled?: (compileMs: number) => void;
      registerWebGPUCompatibility?: () => void;
    } = {},
  ): Promise<void> {
    const warmupPromise = racerVisualReady.then(async () => {
      hooks.onWarmupStart?.();
      try {
        hooks.registerWebGPUCompatibility?.();
        AssetSystem.initSceneTextures(renderer, scene);
        const compileStartedAt = performance.now();
        await renderer.compileAsync(scene, camera);
        hooks.onShadersCompiled?.(performance.now() - compileStartedAt);
      } catch (err) {
        console.warn('[boot] compileAsync failed, falling back to lazy compile', err);
      }
    });
    return Promise.race([
      warmupPromise,
      new Promise<void>((resolve) => setTimeout(resolve, AssetSystem.WARMUP_TIMEOUT_MS)),
    ]);
  }
}
